// DuplicateCartonDialog.jsx — V2-AC-26 (#87): the duplicate warning.
//
// Shown when saving a carton to the library, or sharing it, finds one with the
// same die and the same artwork (409 duplicate_carton). It lists the existing
// cartons with an Open button each, and offers Cancel (the default) or
// "Save anyway" / "Share anyway". It never blocks: the choice is the user's.
// The words and the list come from libraryDuplicates.duplicateDialogModel.

import React from 'react';
import { useTheme } from './theme';

/**
 * @param {Object|null} pending  { model } (duplicateDialogModel) or null when hidden
 * @param {boolean} busy         a "save anyway" is in flight
 * @param {() => void} onConfirm save or share anyway
 * @param {() => void} onCancel  leave everything as it was
 * @param {(id: string) => void} onOpen  show that carton in the library
 */
export default function DuplicateCartonDialog({ pending, busy = false, onConfirm, onCancel, onOpen }) {
  const { P, FN, MO, DISPLAY } = useTheme();
  const cancelRef = React.useRef(null);
  const open = !!pending;

  // Cancel takes the focus once per warning shown — not on every re-render.
  React.useEffect(() => {
    if (open && cancelRef.current) cancelRef.current.focus();
  }, [open, pending]);
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!pending) return null;
  const m = pending.model;
  const btn = {
    fontFamily: MO, fontSize: 11.5, padding: '8px 16px', borderRadius: 6,
    cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
  };

  return (
    <div onClick={busy ? undefined : onCancel} style={{
      position: 'fixed', inset: 0, zIndex: 210, background: 'rgba(20,16,12,.45)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '10vh 16px',
    }}>
      <div role="dialog" aria-modal="true" aria-label={m.title}
        onClick={(e) => e.stopPropagation()} style={{
          width: '100%', maxWidth: 520, background: P.sheet, border: `0.5px solid ${P.rule}`,
          borderRadius: 14, overflow: 'hidden', boxShadow: '0 24px 60px rgba(20,16,12,.28)',
        }}>
        <div style={{ padding: '16px 18px 10px' }}>
          <div style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', color: P.warn }}>
            {m.title}
          </div>
          {m.subject && (
            <div style={{
              fontFamily: DISPLAY, fontSize: 20, color: P.ink, lineHeight: 1.2, marginTop: 4,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{m.subject}</div>
          )}
          <div style={{ fontFamily: FN, fontSize: 12, color: P.ink, lineHeight: 1.5, marginTop: 8 }}>
            {m.message}
          </div>
        </div>

        <div style={{ padding: '0 18px 16px' }}>
          {m.items.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              {m.items.map((it) => (
                <div key={it.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 7,
                  background: P.elev, border: `0.5px solid ${P.ruleSoft}`,
                }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{
                      fontFamily: MO, fontSize: 12, color: P.ink,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{it.name}</div>
                    <div style={{ fontFamily: FN, fontSize: 10.5, color: P.dim, marginTop: 2 }}>
                      {[it.ownerName, it.shared ? 'Shared with your team' : 'Private',
                        it.createdAt ? `added ${it.createdAt.slice(0, 10)}` : null]
                        .filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <button onClick={() => onOpen(it.id)} disabled={busy} style={{
                    ...btn, padding: '5px 11px', fontSize: 10.5,
                    background: 'transparent', color: P.accent, border: `0.5px solid ${P.accentLine}`,
                  }}>Open</button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button ref={cancelRef} onClick={onCancel} disabled={busy} style={{
              ...btn, background: P.ink, color: P.sheet, border: `0.5px solid ${P.ink}`,
            }}>{m.cancelLabel}</button>
            <button onClick={onConfirm} disabled={busy} style={{
              ...btn, background: 'transparent', color: P.muted, border: `0.5px solid ${P.rule}`,
            }}>{busy ? 'Saving…' : m.confirmLabel}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
